import { List, Map, Record } from 'immutable';
import { isEmpty } from 'lodash';
import createCachedSelector from 're-reselect';

import { FALLBACK_LANG } from 'app/lang/constants';
import {
  getSportAdditionalLineIdConf,
  getSportLinesConf,
  getSportLinesStakeTypesConf,
  isFilteredLineStake,
} from 'app/lib/matchGridStakes';

const emptyMap = Map();
const emptyList = List();

/**
 * @type {InnerLineEntry}
 * @property {number} id
 * @property {number} matchId
 * @property {number} index
 * @property {Partial<Record<SupportedLang, string>>} names
 * @property {boolean} isMain
 * @property {'main' | 'regular' | 'additional' | null} kind
 * @property {array} stakeIds
 */
export const InnerLineEntry = Record(
  {
    id: null,
    matchId: null,
    index: null,
    names: null,
    isMain: false,
    kind: null,
    stakeIds: [],
  },
  'InnerLineEntry',
);

export default (matchSportId, matchStakesSelector) => {
  /**
   * Create "map of `stakeIds` by `lineId` for match's innerLines" selector.
   *
   * > [lineId]: [stakeId, ...]
   *
   * @param {number} matchId
   * @return {function(state:object):Immutable.Map<number, number[]>}
   */

  const innerStakeIdsByLineIdSelector = createCachedSelector(
    (state, matchId) => matchSportId(state, { matchId }),
    matchStakesSelector,
    (state, matchId) => getSportLinesStakeTypesConf(matchSportId(state, { matchId })),
    /**
     * @param {number} sportId
     * @param {Immutable.List<StakeEntity>} stakes
     * @param {SportLineStakeTypesConf[]} sportLinesStakeTypesConf
     */
    (sportId, stakes, sportLinesStakeTypesConf) => {
      return stakes.reduce((stakesByLineId, stake) => {
        const matchLines = sportLinesStakeTypesConf.filter((lineStakeTypesConf) => {
          return (
            lineStakeTypesConf?.stakeTypes?.find((stakeTypeConf) => {
              // `stakeTypeConf` can be `number` or `object<{stakeType: number, filter: object}>`
              const stakeType = stakeTypeConf.stakeType || stakeTypeConf;
              const filter = stakeTypeConf.filter;

              const isCorrectStakeType = stakeType === stake.type;

              return isEmpty(filter)
                ? isCorrectStakeType
                : isCorrectStakeType && isFilteredLineStake(filter, stake.params);
            })
          );
        });

        const lineIds = matchLines.length === 0
          ? [getSportAdditionalLineIdConf(sportId)]
          : matchLines.map((line) => line.lineId);

        return lineIds.reduce((map, lineId) => {
          const stakeIds = map.get(lineId) || [];
          stakeIds.push(stake.id);

          return map.set(lineId, stakeIds);
        }, stakesByLineId);
      }, emptyMap);
    },
  )((state, matchId) => matchId);

  /**
   * Create "list of all inner match lines" selector.
   *
   * @param {number} matchId
   * @return {function(state:object):Immutable.List<InnerLineEntry>}
   */

  return createCachedSelector(
    (state, matchId) => matchId,
    innerStakeIdsByLineIdSelector,
    (state, matchId) => matchSportId(state, { matchId }),
    /**
     * @param {number} matchId
     * @param {Immutable.Map<number, number[]>} stakeIdsByLineId
     * @param {number} sportId
     */
    (matchId, stakeIdsByLineId, sportId) => {
      return getSportLinesConf(sportId)
        .reduce((list, line) => {
          const innerLineStakeIds = stakeIdsByLineId.get(line.id) || [];

          return list.push(
            InnerLineEntry({
              ...line,
              matchId,
              names: line.names || { [FALLBACK_LANG]: '--' },
              stakeIds: innerLineStakeIds,
            }),
          );
        }, emptyList)
        .filter((line) => line.stakeIds.length > 0);
    },
  )((state, matchId) => matchId);
};
